import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  ["About", "about"],
  ["Services", "services"],
  ["Process", "process"],
  ["Ask", "ask"],
  ["Reels", "reels"],
  ["FAQ", "faq"],
];

export default function Nav({ isLanding = true }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.2,
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when menu open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // On non-landing pages the anchor links must go home first.
  const anchorHref = (id) => (isLanding ? `#${id}` : `/#${id}`);
  const contactHref = isLanding ? "#contact" : "/#contact";

  return (
    <>
      <motion.div
        style={{ scaleX: progress }}
        className="fixed top-0 left-0 right-0 h-[2px] origin-left z-[70] bg-[#D4AF37]"
        data-testid="scroll-progress-bar"
      />
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-[65] transition-[background,backdrop-filter,border-color] duration-500 ${
          scrolled || !isLanding || menuOpen
            ? "backdrop-blur-2xl bg-black/60 border-b border-white/5"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-2 group"
            onClick={() => setMenuOpen(false)}
          >
            <span className="w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_18px_rgba(212,175,55,0.7)]" />
            <span className="font-display tracking-tight text-[17px] text-white">
              Rubiemaze<span className="text-[#D4AF37]">.</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-9 text-[13px] tracking-wide text-white/70">
            {NAV_LINKS.map(([label, id]) => (
              <a
                key={id}
                href={anchorHref(id)}
                data-testid={`nav-link-${id}`}
                className="gold-underline hover:text-white transition-colors"
              >
                {label}
              </a>
            ))}
            <Link
              to="/blog"
              data-testid="nav-link-blog"
              className="gold-underline hover:text-white transition-colors"
            >
              Journal
            </Link>
          </nav>

          {/* Desktop CTA */}
          <a
            href={contactHref}
            data-testid="nav-cta-book"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black px-4 py-2 text-[13px] font-medium hover:bg-[#f3d568] transition-colors"
          >
            Book Consultation
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </a>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-2">
            <a
              href={contactHref}
              data-testid="nav-cta-book-mobile"
              className="inline-flex items-center rounded-full bg-[#D4AF37] text-black px-3 py-1.5 text-[12px] font-medium"
              onClick={() => setMenuOpen(false)}
            >
              Book
            </a>
            <button
              type="button"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              data-testid="nav-menu-toggle"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-10 h-10 flex flex-col items-center justify-center gap-[5px] rounded-full border border-white/15 text-white"
            >
              <span
                className={`block w-4 h-px bg-current transition-transform duration-300 ${
                  menuOpen ? "translate-y-[3px] rotate-45" : ""
                }`}
              />
              <span
                className={`block w-4 h-px bg-current transition-transform duration-300 ${
                  menuOpen ? "-translate-y-[3px] -rotate-45" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-[64] md:hidden pt-[68px] bg-[#050505]/95 backdrop-blur-2xl"
          >
            <motion.nav
              initial={{ y: -12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -12, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col px-6 py-8"
            >
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/40 mb-6">
                Navigate
              </div>
              <ul className="flex flex-col divide-y divide-white/8">
                {NAV_LINKS.map(([label, id], i) => (
                  <li key={id}>
                    <a
                      href={anchorHref(id)}
                      data-testid={`mobile-nav-${id}`}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-baseline justify-between py-5 group"
                    >
                      <span className="font-display text-white text-3xl group-hover:text-[#D4AF37] transition-colors">
                        {label}
                      </span>
                      <span className="text-[#D4AF37]/60 text-xs tracking-[0.28em]">
                        0{i + 1}
                      </span>
                    </a>
                  </li>
                ))}
                <li>
                  <Link
                    to="/blog"
                    data-testid="mobile-nav-blog"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-baseline justify-between py-5 group"
                  >
                    <span className="font-display text-white text-3xl group-hover:text-[#D4AF37] transition-colors">
                      Journal
                    </span>
                    <span className="text-[#D4AF37]/60 text-xs tracking-[0.28em]">
                      0{NAV_LINKS.length + 1}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/questions"
                    data-testid="mobile-nav-questions"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-baseline justify-between py-5 group"
                  >
                    <span className="font-display text-white text-3xl group-hover:text-[#D4AF37] transition-colors">
                      Q&amp;A Wall
                    </span>
                    <span className="text-[#D4AF37]/60 text-xs tracking-[0.28em]">
                      0{NAV_LINKS.length + 2}
                    </span>
                  </Link>
                </li>
              </ul>

              <a
                href={contactHref}
                data-testid="mobile-nav-cta"
                onClick={() => setMenuOpen(false)}
                className="mt-10 inline-flex items-center justify-center gap-3 rounded-full bg-[#D4AF37] text-black px-6 py-4 text-sm font-medium"
              >
                Book Consultation
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M13 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                </svg>
              </a>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
